import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Empty, Typography, UploadProps, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import Upload, { RcFile } from 'antd/es/upload';
import { useTranslation } from 'react-i18next';
import { IDocument } from 'interfaces';
import api from 'api';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsOpenViewer } from 'store/slices/viewerSlice';
import {
  checkFileNames,
  closeEntityModal,
  deleteTargetEntity,
  fetchAvailableDocuments,
  handleDeleteChoosenObjects,
  reloadSelectedRows,
  resetContextMenuField,
  setDefaultView
} from 'store/reducers/documentsCreator';
import {
  setCurrentFolder,
  setDocuments,
  setDocumentsType,
  setEmptyText,
  setSelectedDocument,
  setViewType,
  addToFileList,
  removeFileFromList,
  setIsAddedViaDragger,
  setAppliedFilters,
} from 'store/slices/dataDocumentsSlice';
import { buildDocumentViewUrl, cleanDocumentViewUrl, parseFiltersFromUrl } from 'helpers/documentFiltersUrl';
import {
  setDisplayedDragger,
  setIsOpenDeleteEntityModal,
  setIsOpenMoveEntityModal,
  setIsOpenRenameEntityModal,
  setSelectedKeys,
  setIsOpenShareEntityModal,
  setMoreInfoHidden,
  setIsOpenNewVersionModal,
  setIsVisibleUploadModal,
  setDocsSegment,
  setIsRSidebarCollapsed,
  setIsFilesSearchingMode,
  setIsOpenPostfixModal,
} from 'store/slices/windowStateSlice';
import { resetDataForPermissionTable } from 'store/slices/dataPermissionsSlice';
import { ConfidentModal, MoveModal, ConfirmationModal, UploadModal, RenameModal, PostfixModal } from 'components/Modals';
import DocumentTable from 'components/DocumentTable/DocumentTable';
import ShareModal from 'components/Modals/ShareModal/ShareModal';
import { setSelectedChatRoom } from 'store/slices/chatsSlice';
import NewVersionModal from 'components/Modals/NewVersionModal/NewVersionModal';
import DetailsComponent, { rightTabOfRS, sectionMatching } from 'components/DetailsComponent/DetailsComponent';
import { documnentIcons, getCorrectIcon } from 'helpers/documentIcons';
import { createFilePath, replaceNonuniqFolder } from 'helpers/replaceNonuniqFolder';
import { renderSelectedEntities } from 'helpers/renderSelectedEntities';
import { refreshFoldersPermissions } from 'helpers/refreshFoldersPermissions';
import { TFunction } from 'i18next';
import JSZip from 'jszip';
import classes from './Documents.module.scss';
import { MessageInstance } from 'antd/es/message/interface';
import EntityInfoModal from 'components/Modals/EntityInfoModal/EntityInfoModal';

export const getDownloadMessage = (filename: string, success: boolean, t: TFunction, messageApi?: MessageInstance) => {
  const messageBuller = messageApi || message;
  if (success) {
    messageBuller.success(<div>
      <span>{documnentIcons[getCorrectIcon(filename?.split('.').reverse()[0])]}</span>
      {filename} - {t('Documents.contextMenu.successDownload')}
    </div>, 5);
  } else {
    messageBuller.error(`${t('Documents.error.downloadFailed')} '${filename}'`);
  }
};

export default function Documents() {
  const [dragLeaveTimer, setDragLeaveTimer] = React.useState<NodeJS.Timeout>();
  const [needToCheckCurrentDocuments, setNeedToCheckCurrentDocuments] = React.useState(false);
  const [duplicateDocuments, setDuplicateDocuments] = React.useState<{
    forUpload: RcFile[]; forDelete: IDocument[]; generatedNames: string[];
  }>({
    forDelete: [], forUpload: [], generatedNames: [],
  });
  
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { viewerAbortController } = useAppSelector(state => state.viewer);
  const { userFoldersPermissions, userRootPermissions, foldersForPermissions } = useAppSelector((store) => store.permissions);
  const { selectedRoom, userPermissions } = useAppSelector(state => state.userData);
  const { documents, folders, viewType, documentsType, currentFolder, selectedDocument,
    fileList, entityOfContextMenu, errorDocuments, folderFilesCount,choosenDocuments
  } = useAppSelector((store) => store.documents);
  const { isOpenDeleteEntityModal, isDeletingDocuments, isMovingDocument, displayedDragger, 
    isOpenMoveEntityModal, isOpenInfoEntityModal, isLoadingProcess, isOpenRenameEntityModal,
    isOpenShareEntityModal, isOpenNewVersionModal, isVisibleUploadModal, isOpenConfidentConfirm,
    isOpenPostfixModal, messageApi,
  } = useAppSelector((store) => store.windowState);  
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { t } = useTranslation();
  const currentFolderId = useParams().folderId || null;
  const parentFolderID = currentFolder[0] ? currentFolder[0]?.id : null;

  let documentNameWithVersions: string;
  if (isOpenDeleteEntityModal && entityOfContextMenu?.type !== 'folder') {
    const targetDoc = entityOfContextMenu as IDocument;
    const versionsCnt = targetDoc?.version_count && targetDoc?.version_count > 1
      ? `(+${targetDoc?.version_count - 1} ${t('Documents.management.versionsCnt')})` : '';
    documentNameWithVersions = String(entityOfContextMenu?.name) + versionsCnt;
  };

  const currentFolderWithPermissions = parentFolderID 
    ? userFoldersPermissions?.find(folder => folder.id === parentFolderID) || foldersForPermissions?.find(folder => folder.id === parentFolderID)
    : userRootPermissions;

  const isAvailableUploading = currentFolderWithPermissions?.permissions && currentFolderWithPermissions?.permissions?.includes('can_upload');
  
  const fileCase = !documentsType ? 'files' : documentsType;
  const uploadFilesCases = {
    files: {
      title: t('Documents.modals.upload.files'),
      isConfidential: false,
    },
    confidentialFiles: {
      title: t('Documents.modals.upload.confidentialFiles'),
      isConfidential: true,
    }
  };

  const listenPopstate = React.useCallback(() => {
    dispatch(setIsOpenViewer(false));
    dispatch(setDefaultView());
  }, []);

  React.useEffect(() => {
    const detailsSections = Object.keys(sectionMatching);
    const document_id = localStorage.getItem('document_id') || searchParams.get('document_id');
    const webviewer_file_id = localStorage.getItem('webviewer_file_id') || searchParams.get('webviewer_file_id'); // previous logic
    let modalSection = localStorage.getItem('modalSection') || searchParams.get('modalSection');
    const targetDocument = documents?.find(doc => doc.id === document_id);    
    
    if (documents[0] && targetDocument && document_id && modalSection) {
      const isDetails = detailsSections?.includes(modalSection);
      if (!isDetails) modalSection = 'view';
      if (targetDocument) {
        const rightSection = sectionMatching[modalSection as keyof typeof sectionMatching];
        dispatch(setSelectedDocument(targetDocument));
        dispatch(setViewType('Document'));
        navigate(buildDocumentViewUrl(window.location.pathname, searchParams, modalSection, targetDocument.id));
        if (rightSection === 'signing' || rightSection === 'agreement') {          
          let neededPermission: boolean;
          if (rightSection === 'agreement') {
            neededPermission = userPermissions.can_edit_agreement;
          } else if (rightSection === 'signing') {
            neededPermission = userPermissions.can_manage_signing;
          } else {
            neededPermission = true;
          }

          neededPermission &&dispatch(setIsRSidebarCollapsed({
            collapsed: false,
            entity: rightTabOfRS[rightSection as keyof typeof rightTabOfRS],
            width: 350,
          }))
        } else {
          !selectedDocument && dispatch(setDocsSegment(rightSection))
        }

        localStorage.removeItem('document_id');
        localStorage.removeItem('modalSection');
      };
    } else if (documents[0] && targetDocument && document_id && webviewer_file_id) {
      dispatch(setSelectedDocument(targetDocument));
      dispatch(setViewType('Document'));
      navigate(buildDocumentViewUrl(window.location.pathname, searchParams, 'view', targetDocument.id));

      localStorage.removeItem('document_id');
      localStorage.removeItem('webviewer_file_id');
    }
    dispatch(reloadSelectedRows({}));
  }, [documents, currentFolder, searchParams.get('document_id')])

  React.useEffect(() => {
    window.addEventListener('popstate' ,listenPopstate);
    const webviewerFileId = searchParams.get('webviewer_file_id');
    if (webviewerFileId && viewType === 'Document') {
      api.getDocumentById(webviewerFileId).then((res) => {
        dispatch(setSelectedDocument(res.data));
        dispatch(setDocsSegment('view'));
      }).catch(() => {
        dispatch(setDefaultView());
        dispatch(setDocsSegment('view'));
      });
    } else {
      dispatch(setDefaultView());
      dispatch(setDocsSegment('view'));
    }
    dispatch(setSelectedChatRoom(null));
    return () => {
      window?.removeEventListener('popstate', listenPopstate);
      setNeedToCheckCurrentDocuments(false);
    };
  }, []);

  React.useEffect(() => {
    dispatch(resetContextMenuField({}));
    if (viewType === 'Table' && viewerAbortController) {
      viewerAbortController.abort();
    };
  }, [viewType]);

  React.useEffect(() => {
    if (needToCheckCurrentDocuments) {
      checkAllFileNames(fileList as RcFile[]);
      setNeedToCheckCurrentDocuments(false);
    };
  }, [needToCheckCurrentDocuments]);

  React.useEffect(() => {
    let promiseDocuments: any;

    const handleFolderAccess = async () => {
      dispatch(setSelectedKeys(['documents']));
      dispatch(setDocumentsType(null));
      dispatch(setIsFilesSearchingMode(false));
      window.scroll(0,0);

      if (dataRoom?.is_blocked) {
        dispatch(setEmptyText('blocked'));
      } else if (dataRoom && userFoldersPermissions && (dataRoom.id === params.room || dataRoom.custom_url === params.room)) {
        const urlFilters = parseFiltersFromUrl(searchParams);
        dispatch(setAppliedFilters(urlFilters));

        if ( currentFolderId ) {
          const isUserHavePermissionsToView = currentFolderWithPermissions?.permissions?.length > 0;
          if (isUserHavePermissionsToView){
            const currentFolderIdx = currentFolder.findIndex(({ id }) => id === currentFolderId);
            dispatch(setCurrentFolder(currentFolder.slice(0, currentFolderIdx + 1)));
            promiseDocuments = dispatch(fetchAvailableDocuments({ roomId: dataRoom.id, folderId: currentFolderId, filters: urlFilters }));
          } else {
            await refreshFoldersPermissions(dataRoom.id, dispatch);
            dispatch(setEmptyText('noPermissionToView'));
            dispatch(setDocuments({ documents: [], folders: [] }));
          }
        } else if ( currentFolderId === null ) {
          dispatch(setCurrentFolder([]));
          promiseDocuments = dispatch(fetchAvailableDocuments({ roomId: dataRoom!.id, folderId: null, filters: urlFilters }));
        }
      };
    };

    handleFolderAccess();

    dispatch(reloadSelectedRows({}));

    return () => {
      dispatch(setMoreInfoHidden(true));
      dispatch(setIsOpenViewer(false));
      dispatch(setEmptyText('fallback'));
      dispatch(resetDataForPermissionTable());
      // Перенесено в компонент RoomsTab >> onOpenClick для решения задачи №20864
      // dispatch(setDefaultView());

      promiseDocuments?.abort();
      // при возврате в документы всегда направляем в коревую папку
      // dispatch(setCurrentFolder([]));
    }
  }, [dataRoom?.id, currentFolderId, userFoldersPermissions])

  React.useEffect(() => {
    if (errorDocuments) messageApi?.error(t('Documents.error.receivingData'), 10)
  }, [errorDocuments]);

  const checkAllFileNames = async (allFiles: RcFile[]) => {
    try {
      const result = await dispatch(checkFileNames({ allFiles, setDuplicateDocuments })).unwrap();
      return result;
    } catch {
      return { hasDuplicates: false };
  }
  };

  const beforeUpload = async (file: RcFile, allFiles: RcFile[], type?: string) => {
    const allFileNames = fileList.map(file => file.name);
    const thisDirFolderNames = folders.map((folder => folder.name));

    const checkIsNeedToAddThisFile = (file: RcFile) => {
      const currentExtension = file.name.match(/\.(\w+)$/)![1].toLowerCase();

      if (allFileNames.includes(file.name)) {
        messageApi?.warning(t('Documents.error.alreadyAdded', {fileName: file.name}));
        return false;
      }
      
      if (file.size > 500 * 1024 * 1024) {
        messageApi?.error(t('Documents.error.uploadDocumentSize'));
        return false;
      }
      
      if (file.size === 0) {
        messageApi?.error(t('Documents.error.uploadDocumentSizeZero', {fileName: file.name}), 10);
        return false;
      }
      
      if (currentExtension === 'exe' || currentExtension === 'bin') {
        messageApi?.error(t('Documents.error.notSupported'));
        return false;
      }      

      if (file.name.toLowerCase().replace(`.${currentExtension}`, '').length > 150) {
        messageApi?.error(t('Documents.error.fileNameTooLong'));
        return false;
      }

      return true;
    }

    if (type === 'dragger') {
      dispatch(setIsAddedViaDragger(true));
      dispatch(setIsVisibleUploadModal(true));
    }
    try {
      const extension = file.name.match(/\.(\w+)$/)![1].toLowerCase();

      if (extension === 'zip' && type === 'anarchive') {
        const rootFolderName = file.name.replace('.zip', '');        
        const zip = new JSZip();
        let arrWithFiles: RcFile[] = []
        zip.loadAsync(file, {createFolders: true}).then(async(res: JSZip) => {          
          const keys = Object.keys(res.files); // Gets keys of objects (on res.files) inside which file data is located          
          for (let key of keys) {
            //@ts-ignore
            // console.log(res.files[key]._data.uncompressedSize, '<<._data.uncompressedSize;');
            const isFileInRoot = !res.files[key]?.name.includes(rootFolderName);
            const isRealFile = isFileInRoot || res.files[key]?.name.startsWith(rootFolderName);
            const isDir = res.files[key]?.dir;
            const result = isRealFile && !isDir && await res.files[key].async('blob');
            if (result) {
              const ext = res.files[key]?.name?.split('.').reverse()[0];
              const fileNameArr = res.files[key]?.name.split('/');
              var file = new File([result], [...fileNameArr].reverse()[0], {type: ext});
              
              Object.defineProperty(file, 'webkitRelativePath', {
                value: createFilePath(fileNameArr, thisDirFolderNames),
              });
              if (checkIsNeedToAddThisFile(file as RcFile)) {
                arrWithFiles.push(file as RcFile);
              }
            }
          }
          dispatch(addToFileList(arrWithFiles));
        });
        return false;
      };

      if (checkIsNeedToAddThisFile(file)) {
        let currentFile = replaceNonuniqFolder(file, thisDirFolderNames);
        dispatch(addToFileList([currentFile]));
      }

      if (file.uid === allFiles[allFiles.length - 1].uid && type !== 'dragger'){
        setNeedToCheckCurrentDocuments(true);
      };
      return false;
    } catch (err) {
      console.log(err);
      return false;
    };
  }

  const onRemoveFile = (fileId: string) => {
    dispatch(removeFileFromList([fileId]))
  };

  const uploadProps: UploadProps = {
    multiple: true,
    onRemove: (file) => onRemoveFile(file.uid),
    beforeUpload,
    fileList,
  };

  const folderUploadProps: UploadProps = {
    multiple: true,
    onRemove: (file) => onRemoveFile(file.uid),
    beforeUpload,
    directory: true,
    fileList: [],
  };

  const onClosePostfixModal = () => {
    dispatch(setIsOpenPostfixModal(false));
    dispatch(setIsVisibleUploadModal(true));
    const cancelFilelists  = duplicateDocuments.forUpload.map(({ uid }) => uid);
    dispatch(removeFileFromList(cancelFilelists))
    setDuplicateDocuments({ forDelete: [], forUpload: [], generatedNames: [] });
  }

  const closeAllModals = () => {
    dispatch(closeEntityModal({navigate}));
  };

  const preventDefault = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropEvent = (e: any) => {    
    dispatch(setDisplayedDragger(false));
    preventDefault(e);
  };

  const handleEnterEvent = (e: any) => {
    const types = e.dataTransfer?.types || [];
    const hasFiles =
      types.includes?.('Files') ||
      e.dataTransfer?.files?.length > 0 ||
      (types.length > 0 && String(types).toLowerCase().includes('file'));

    if (hasFiles) {
      clearInterval(dragLeaveTimer);
      dispatch(setDisplayedDragger(true));
      preventDefault(e);
    }
  };

  const onDragOver = (e: any) => {
    clearInterval(dragLeaveTimer);
    preventDefault(e);
  };

  const onDragLeave = (e: any) => {
    clearInterval(dragLeaveTimer);
    setDragLeaveTimer(setTimeout(() => dispatch(setDisplayedDragger(false)), 330));
    preventDefault(e);
  };

  const DocumentView = {
    Document: (<DetailsComponent />),
    Table: (<DocumentTable />),
    Report: null,
  };

  const { namesOfChoosenFiles, namesOfChoosenFolders } = React.useMemo(
    () => renderSelectedEntities(choosenDocuments, documents, folders, t),
    [choosenDocuments, documents, folders, t]
  );

  const isBulkMode = choosenDocuments?.length > 1;
  const deleteSubmit = () => {
     if (isBulkMode) {
      dispatch(handleDeleteChoosenObjects({
        forDeleteDocuments: choosenDocuments,
        currentFolderId,
        t,
      }));
      dispatch(setIsOpenDeleteEntityModal(false));
      return;
    }

    const message = (text: string) => {
      return <div>
        <span>{documnentIcons[getCorrectIcon(entityOfContextMenu?.extension || 'folder')]}</span> {entityOfContextMenu?.name} - {text}
      </div>
    }
    dispatch(deleteTargetEntity({
      entity: entityOfContextMenu!,
      t,
      navigate: () => navigate(cleanDocumentViewUrl(window.location.pathname, searchParams)),
      message: message
    }));
  };
  
  return (
    <div
      className={classes.wrapper}
      onDrop={(e) => isAvailableUploading && handleDropEvent(e)}
      onDragOver={(e) => isAvailableUploading && onDragOver(e)}
      onDragEnter={(e) => isAvailableUploading && handleEnterEvent(e)}
      onDragLeave={(e) => isAvailableUploading && onDragLeave(e)}
    >
      <div className={classes.mainSpace}>{DocumentView[viewType]}</div>

      {isVisibleUploadModal && (
        <React.Suspense fallback={<div />}>
          <UploadModal
            open={isVisibleUploadModal}
            entityName={dataRoom?.name}
            uploadProps={uploadProps}
            fileList={fileList}
            folderUploadProps={folderUploadProps}
            title={uploadFilesCases[fileCase]?.title}
            beforeUpload={beforeUpload}
            checkAllFileNames={checkAllFileNames}
          />
        </React.Suspense>
      )}

      {isOpenConfidentConfirm && (
        <React.Suspense fallback={<div />}>
          <ConfidentModal
            open={isOpenConfidentConfirm}
            selectedDocument={selectedDocument!}
            roomId={selectedRoom?.id!}
            t={t}
          />
        </React.Suspense>
      )}

      <Upload.Dragger
        className={
          displayedDragger && !isMovingDocument && !isOpenNewVersionModal
            ? classes.dropDownActive
            : classes.dropDownNone
        }
        openFileDialogOnClick={false}
        showUploadList={false}
        directory
        {...uploadProps}
        beforeUpload={(file, allFiles) =>
          beforeUpload(file, allFiles, 'dragger')
        }
      >
        <Empty rootClassName={classes.emptyElement} image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Documents.modals.upload.dropText')} />
      </Upload.Dragger>

      <React.Suspense fallback={<div />}>
        <ConfirmationModal
          isOpen={isOpenDeleteEntityModal}
          onCancel={
            viewType === 'Table'
              ? closeAllModals
              : () => dispatch(setIsOpenDeleteEntityModal(false))
          }
          isLoading={isDeletingDocuments}
          submit={deleteSubmit}
          text={
            isBulkMode ? (
              <div>
                <span>{t('Documents.confirm.confirmDelete')}:</span>
                {namesOfChoosenFolders}
                {namesOfChoosenFiles}
              </div>
            ) : entityOfContextMenu?.type === 'folder' ? (
              <div>
                {`${t('Documents.modals.delete.confirmFolder')}${entityOfContextMenu?.name}?`}
                {typeof folderFilesCount === 'number' &&
                  folderFilesCount > 0 && (
                    <div
                      className={classes.deleteWarning}
                      style={{ marginTop: 8 }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                        }}
                      >
                        <ExclamationCircleOutlined
                          style={{ color: 'var(--error, #e14d4d)' }}
                        />
                        <Typography.Text>
                          {t('Documents.modals.delete.folderDeletionNotice')}
                        </Typography.Text>
                      </div>
                      <Typography.Text className={classes.deleteWarningCounter}>
                        {t('Documents.modals.delete.folderContainsFiles')}{' '}
                        <Typography.Text strong>
                          {folderFilesCount}
                        </Typography.Text>
                      </Typography.Text>
                    </div>
                  )}
              </div>
            ) : (
              `${t('Documents.modals.delete.confirmFile')}${documentNameWithVersions!}?`
            )
          }
          title={`${t('Documents.modals.delete.deletion')} 
            ${
              entityOfContextMenu?.type === 'folder'
                ? t('Documents.modals.delete.folder')
                : t('Documents.modals.delete.file')
            }`}
        />
      </React.Suspense>

      <React.Suspense fallback={<div />}>
        {isOpenRenameEntityModal && (
          <RenameModal
            open={isOpenRenameEntityModal}
            onCancel={
              viewType === 'Table'
                ? closeAllModals
                : () => dispatch(setIsOpenRenameEntityModal(false))
            }
            entity={entityOfContextMenu!}
            type={
              entityOfContextMenu?.type === 'folder' ? 'Folder' : 'Document'
            }
          />
        )}
      </React.Suspense>

      <React.Suspense fallback={<div />}>
        {isOpenShareEntityModal && (
          <ShareModal
            open={isOpenShareEntityModal}
            onCancel={
              viewType === 'Table'
                ? closeAllModals
                : () => dispatch(setIsOpenShareEntityModal(false))
            }
            entity={entityOfContextMenu!}
          />
        )}
      </React.Suspense>

      <React.Suspense fallback={<div />}>
        {isOpenMoveEntityModal && (
          <MoveModal
            isOpen={isOpenMoveEntityModal}
            onCancel={
              viewType === 'Table'
                ? closeAllModals
                : () => dispatch(setIsOpenMoveEntityModal(false))
            }
            isLoading={isLoadingProcess}
          />
        )}
      </React.Suspense>

      <React.Suspense>
        {isOpenPostfixModal && (
          <PostfixModal
            duplicateDocuments={duplicateDocuments}
            isOpen={isOpenPostfixModal}
            onCancel={onClosePostfixModal}
            t={t}
          />
        )}
      </React.Suspense>

      <React.Suspense fallback={<div />}>
        {isOpenNewVersionModal && (
          <NewVersionModal
            isOpen={isOpenNewVersionModal}
            onCancel={
              viewType === 'Table'
                ? closeAllModals
                : () => dispatch(setIsOpenNewVersionModal(false))
            }
            entity={selectedDocument as IDocument}
          />
        )}
      </React.Suspense>

      <React.Suspense>
        {isOpenInfoEntityModal && (
          <EntityInfoModal
            isOpen={isOpenInfoEntityModal}
          />
        )}
      </React.Suspense>
    </div>
  );
}
